"use client";

import React, { useEffect, useState } from "react";

import Link from "next/link";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { GROQCTA } from "@/sanity/lib/definitions";
import { useCTAWrapper } from "@/hooks/useCTAWrapper";
import Anchor from "@/components/global/utils/Anchor";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/shadcn/ui/dialog";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import HubSpotForm from "@/components/global/partials/HubSpotForm";
import ZapierForm from "@/components/global/partials/ZapierForm";
import Chip from "@/components/global/ui/Chip";
import { cn } from "@/lib/utils";
import DividerGreenLine from "@/components/global/ui/DividerGreenLine";
import { forwardUTMParams, isPlatformUrl } from "@/lib/utm";

// import accessibility from "@/public/icons/Accessibility--alt.svg";

interface CTAWrapperProps {
  cta: GROQCTA | null | undefined;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

interface PlatformAwareLinkProps
  extends Omit<React.ComponentProps<typeof Link>, "href"> {
  href: string;
}

function PlatformAwareLink({
  href,
  children,
  ...props
}: PlatformAwareLinkProps) {
  const [finalHref, setFinalHref] = useState(href);

  useEffect(() => {
    if (isPlatformUrl(href)) {
      setFinalHref(forwardUTMParams(href));
    } else {
      setFinalHref(href);
    }
  }, [href]);

  return (
    <Link href={finalHref} {...props}>
      {children}
    </Link>
  );
}

export function CTAWrapper({ cta, children, className, style }: CTAWrapperProps) {
  const { isLink, link, linkData, isAnchor, anchor, isPopup, popup } = useCTAWrapper(cta);

  if (isLink && link) {
    const openInNewTab = linkData?.openInNewTab ?? false;
    return (
      <PlatformAwareLink
        href={link}
        className={cn("inline-block", className)}
        style={style}
        target={openInNewTab ? "_blank" : "_self"}
        rel={openInNewTab ? "noopener noreferrer" : undefined}>
        {children}
      </PlatformAwareLink>
    );
  }

  if (isAnchor && anchor) {
    return (
      <Anchor
        target={anchor}
        className={cn("inline-block", className)}
        style={style}>
        {children}
      </Anchor>
    );
  }

  if (isPopup && popup) {
    const isNewTheme = popup.theme === "new";

    return (
      <Dialog>
        <DialogTrigger
          asChild
          className="">
          <div
            className={cn("w-full md:w-auto", className)}
            style={style}>
            {children}
          </div>
        </DialogTrigger>
        <DialogContent className="overflow-y-auto top-1/2 -translate-y-1/2 lg:max-h-screen max-h-[80svh] rounded-[1.25rem] max-w-[62.5rem]">
          <VisuallyHidden>
            <DialogHeader>
              <DialogTitle></DialogTitle>
              <DialogDescription></DialogDescription>
            </DialogHeader>
          </VisuallyHidden>

          <div className="flex relative flex-col items-center w-full bg-white px-[2.5rem] py-[3rem] rounded-[1rem] overflow-clip md:py-[5rem]">
            {popup?.chip && <Chip text={popup.chip} />}

            {popup?.content && (
              <div className="richtext w-[min(50rem,100%)] mb-[3rem] prose-p:text-black/70 prose-p:text-[2rem] prose-headings:text-black prose-headings:text-[4rem] lg:prose-headings:text-[5rem]">
                <PortableText
                  value={popup.content}
                  components={customRichTextComponent({})}
                />
              </div>
            )}

            {popup?.formType === "hs" && popup?.hsForm && <HubSpotForm formData={popup?.hsForm} />}

            {popup?.formType === "zapier" && popup?.zapierForm && <ZapierForm formData={popup.zapierForm} />}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div
      className={cn("inline-block", className)}
      style={style}>
      {children}
    </div>
  );
}
